import {
  Card,
  CardContent,
  createStyles,
  type Theme,
  Typography,
  withStyles,
  type WithStyles,
} from "@material-ui/core";
import { useTranslations } from "adapters/i18n";
import { ConfigurationItem } from "constants/configurations";
import React from "react";
import LinesEllipsis from "react-lines-ellipsis";
import type { ConfigurationCardItem } from "../configurations.types";
import {
  getConfigurationCardIcon,
  NEW_BADGE_ICON_PATH,
} from "../utils/configurations.utils";

type ConfigurationCardProps = {
  item: ConfigurationCardItem;
  onItemClick: (id: ConfigurationItem) => void;
} & WithStyles<typeof styles>;

function ConfigurationCard({
  classes,
  item,
  onItemClick,
}: ConfigurationCardProps) {
  const { t } = useTranslations();

  const handleClick = () => {
    onItemClick(item.id);
  };

  const cardIconPath = getConfigurationCardIcon(item.id);

  return (
    <React.Fragment>
      <Card className={classes.card}>
        <CardContent
          id={item.id.toString()}
          className={classes.cardContent}
          onClick={handleClick}
        >
          <div className={classes.iconWrapper}>
            <div className={classes.iconBackground}>
              <img src={cardIconPath} alt={item.label} />
            </div>
          </div>
          <div>
            <div className={classes.titleWrapper}>
              <Typography
                className={classes.title}
                id="card-title"
                noWrap={true}
                variant="h4"
              >
                {item.label}
              </Typography>
              {item.hasNewBadge && (
                <img
                  alt={t("common:new")}
                  className={classes.newBadge}
                  src={NEW_BADGE_ICON_PATH}
                />
              )}
            </div>

            <Typography variant="body1" className={classes.cardDescription}>
              <LinesEllipsis
                basedOn="letters"
                ellipsis="..."
                id="card-description"
                maxLine="4"
                text={item.description ?? ""}
                trimRight={true}
                component="span"
              />
            </Typography>
          </div>
        </CardContent>
      </Card>
    </React.Fragment>
  );
}

const styles = (theme: Theme) =>
  createStyles({
    card: {
      minWidth: 300,
      minHeight: 160,
      width: 300,
      height: 160,
      marginTop: "40px",
      borderRadius: 6,
      backgroundColor: "#FAFAFA",
    },
    cardContent: {
      padding: "13px",
      cursor: "pointer",
      display: "flex",
      flexWrap: "nowrap",
    },
    titleWrapper: {
      display: "flex",
    },
    title: {
      fontFamily: "Roboto",
      fontSize: 16,
      fontWeight: 500,
      letterSpacing: "-0.03px",
      color: theme.palette.primary.main,
      margin: "8px 0 13px 0",
      textOverflow: "ellipsis",
      whiteSpace: "normal",
    },
    newBadge: {
      height: 26,
      margin: "8px 0 13px 6px",
    },
    cardDescription: {
      fontFamily: "Roboto",
      fontSize: 14,
      fontWeight: 300,
      lineHeight: 1.43,
      letterSpacing: "-0.03px",
      color: "#2a2a2a",
    },
    iconWrapper: {
      marginRight: 15,
    },
    iconBackground: {
      width: 40,
      height: 40,
      backgroundColor: theme.palette.primary.main,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
  });

export default withStyles(styles)(ConfigurationCard);
